export type PartnerBrand = "leboncoin";

const PARTNER_DOMAINS: Record<PartnerBrand, string> = {
  leboncoin: "multilocal-leboncoin.com",
};

const PARTNER_BRANDS: PartnerBrand[] = ["leboncoin"];

export const PARTNER_NAMES: Record<PartnerBrand, string> = {
  leboncoin: "leboncoin",
};

const OWN_NAME = "Mobsuccess";
const BLANK_ICON = "data:,";

type PartnerBrandLocation = {
  hostname: string;
  search: string;
};

// The partner platform the visitor signs in for: the host serving this app,
// else the `source_host` PHP passes along, else the host `to` returns to.
// Matched against known domains only, so a crafted link cannot show a mark
// that is not listed here.
export function detectPartnerBrand(
  location: PartnerBrandLocation,
): PartnerBrand | null {
  const searchParams = new URLSearchParams(location.search);
  const hostnames = [
    location.hostname,
    readHostname(searchParams.get("source_host")),
    readHostname(searchParams.get("to")),
  ];
  return (
    PARTNER_BRANDS.find((brand) =>
      hostnames.some((hostname) =>
        isOnDomain(hostname, PARTNER_DOMAINS[brand]),
      ),
    ) ?? null
  );
}

// `data-partner` on <html>, set once at boot: `to` leaves the URL with the
// first navigation, the attribute stays for the whole visit. A partner
// platform is served white-label, so the tab loses the Mobsuccess name and
// icon that index.html ships.
export function applyPartnerBrand(brand: PartnerBrand | null): void {
  if (brand === null) {
    delete document.documentElement.dataset.partner;
    return;
  }
  document.documentElement.dataset.partner = brand;
  document.title = PARTNER_NAMES[brand];
  document.querySelector('link[rel="icon"]')?.setAttribute("href", BLANK_ICON);
}

export function readPartnerBrand(): PartnerBrand | null {
  const value = document.documentElement.dataset.partner;
  return PARTNER_BRANDS.find((brand) => brand === value) ?? null;
}

export function readBrandName(): string {
  const brand = readPartnerBrand();
  return brand ? PARTNER_NAMES[brand] : OWN_NAME;
}

function readHostname(value: string | null): string | null {
  if (!value) {
    return null;
  }
  try {
    return new URL(value.includes("://") ? value : `https://${value}`).hostname;
  } catch {
    return null;
  }
}

function isOnDomain(hostname: string | null, domain: string): boolean {
  return hostname === domain || (hostname?.endsWith(`.${domain}`) ?? false);
}
